import { useCallback, useEffect, useRef, useState } from "react";
import { useAssistantWidgetStatus } from "stores/useAssistantWidgetStatus";
import { getAssistantReviewStatusKeys } from "../assistantReviewMessage";

const REVIEW_COMPLETION_HOLD_MS = 650;
const TERMINAL_REVIEW_STATUSES = new Set([
  "confirmed",
  "deleted",
  "dismissed",
  "sent",
  "cancelled",
]);

// Open/close lifecycle for the review card: it opens itself when a turn comes
// back carrying widgets, and closes itself once every widget in that message
// reaches a terminal status. Lifted out of the old overlay so the rules are
// testable without rendering a surface.
const useReviewCardState = ({ opened, reviewMessage, clearReviewMessage }) => {
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const clearAfterExitRef = useRef(false);
  const statusByKey = useAssistantWidgetStatus((state) => state.statusByKey);

  const statusKeys = getAssistantReviewStatusKeys(reviewMessage);
  const allComplete =
    statusKeys.length > 0 &&
    statusKeys.every((key) => TERMINAL_REVIEW_STATUSES.has(statusByKey[key]));

  useEffect(() => {
    if (opened && reviewMessage?.id) {
      clearAfterExitRef.current = false;
      setIsReviewOpen(true);
    }
  }, [opened, reviewMessage?.id]);

  useEffect(() => {
    if (!allComplete) return undefined;

    // Hold the finished state on screen for a beat so the user sees the
    // confirmation land instead of the card vanishing under their cursor.
    const timeoutId = window.setTimeout(() => {
      if (isReviewOpen) {
        clearAfterExitRef.current = true;
        setIsReviewOpen(false);
      } else {
        clearReviewMessage();
      }
    }, REVIEW_COMPLETION_HOLD_MS);

    return () => window.clearTimeout(timeoutId);
  }, [allComplete, clearReviewMessage, isReviewOpen]);

  const openReview = useCallback(() => {
    clearAfterExitRef.current = false;
    setIsReviewOpen(true);
  }, []);

  const collapseReview = useCallback(() => {
    clearAfterExitRef.current = false;
    setIsReviewOpen(false);
  }, []);

  // Only a completion-driven close drops the message; a manual collapse keeps
  // it so "Review draft" can bring the same widgets back.
  const handleReviewExited = useCallback(() => {
    if (!clearAfterExitRef.current) return;
    clearAfterExitRef.current = false;
    clearReviewMessage();
  }, [clearReviewMessage]);

  return { isReviewOpen, openReview, collapseReview, handleReviewExited };
};

export { REVIEW_COMPLETION_HOLD_MS, TERMINAL_REVIEW_STATUSES };
export default useReviewCardState;
